// Inline autocomplete state for the message composer.
//
// Token detection: `@`, `#`, `:` open the menu when typed at the start
// of a word — i.e. preceded by whitespace, a newline, or the start of
// the textarea. The query is everything between the trigger and the
// caret, until a whitespace/newline closes the token. `:` requires a
// minimum query length of 2 to keep the emoji provider quiet on a bare
// colon (a punctuation character users type far more often than they
// open the emoji autocomplete).
//
// Selection: callers receive a `select(replacement, opts)` callback
// that swaps the trigger + query for the chosen replacement. We use
// `setRangeText` for the splice so the textarea preserves its native
// undo history — the user can `Cmd+Z` over a mention without losing
// the entire composition. Caret position lands one space past the
// inserted replacement so the user can keep typing without nudging
// the cursor back.
//
// The hook deliberately stays generic: it doesn't know about Matrix,
// users, rooms, or emoji. Providers (see `autocomplete-providers.ts`)
// translate `{ trigger, query }` into a list of suggestions and
// receive `select` to invoke when the user picks one.

import { markdownToMatrixHtml } from "~/lib/markdown-html";
import { useCallback, useEffect, useRef, useState } from "react";

/** Trigger characters the autocomplete recognises. */
export type AutocompleteTrigger = "@" | "#" | ":";

const TRIGGERS: readonly AutocompleteTrigger[] = ["@", "#", ":"] as const;

/** Minimum query length per trigger before suggestions surface. */
const MIN_QUERY_LENGTH: Record<AutocompleteTrigger, number> = {
  "@": 0,
  "#": 0,
  ":": 2,
};

export interface AutocompleteState {
  /** Whether the menu is currently open. */
  open: boolean;
  /** The trigger character that opened the menu. */
  trigger: AutocompleteTrigger | null;
  /**
   * The query the user is typing — everything between the trigger
   * character and the caret, exclusive. Empty on the keystroke that
   * types the trigger itself.
   */
  query: string;
  /**
   * Caret index of the trigger character within the textarea. Used by
   * the UI to anchor the menu next to the caret without re-measuring
   * on every render.
   */
  triggerIndex: number;
}

export interface OwnAgentProvenance {
  ownerMxid: string;
  roomId: string;
  scopeId: string;
}

/** Replacement options the provider passes to `select`. */
export interface AutocompleteSelectOptions {
  /**
   * Plain-text body fragment to insert (e.g. `@Display Name`,
   * `#room`, `:smile:` → 😄). Replaces the trigger + query in the
   * textarea.
   */
  text: string;
  /**
   * Optional HTML fragment for `formatted_body` (Matrix-custom HTML).
   * Tracked separately so the composer can ship a serialised pill
   * without losing the plain-text fallback. When omitted, the plain
   * `text` is used in the formatted body too.
   */
  html?: string;
  /**
   * MXID of a mentioned user, set only for `@` user pills. Recorded on
   * the committed token so the send path can attach
   * `m.mentions.user_ids`. Room / emoji selections leave it unset.
   */
  mxid?: string;
  ownAgent?: OwnAgentProvenance;
  /**
   * Whether the menu should stay open after the selection (Tab) or
   * close (Enter / mouse click). Default: false (close).
   */
  keepOpen?: boolean;
}

/** Minimum query buffer the composer keeps for `formatted_body`
 *  serialisation. Indexed by token start position. */
export interface MentionToken {
  /** Character index of the first character of the inserted plain
   *  text (i.e. where the trigger was). */
  start: number;
  /** Character index immediately after the inserted plain text. */
  end: number;
  /** Plain-text fragment. */
  text: string;
  /** Matrix-custom HTML for the `formatted_body` pill. */
  html: string;
  /** MXID of the mentioned user, present only for user pills. Surfaced
   *  as `m.mentions.user_ids` at send time when the token survives. */
  mxid?: string;
  ownAgent?: OwnAgentProvenance;
}

export interface MentionEdit {
  /** Character index where the edit began in the pre-edit body. */
  start: number;
  /** Number of pre-edit characters replaced or removed. */
  removedLength: number;
  /** Number of characters inserted by the edit. */
  insertedLength: number;
}

/** Result of `serialiseFormattedBody` — the formatted body the
 *  composer pairs with the plain `body` on send. */
export interface SerialisedComposition {
  body: string;
  /** HTML for `formatted_body`; null for plain text with no mentions
   *  (the wrapper drops it before calling the SDK). */
  formattedBody: string | null;
  /** Deduped MXIDs of the surviving user-mention tokens, in document
   *  order. Empty when no user pills survived — the send path then
   *  omits `m.mentions` entirely. */
  mentions: string[];
  /** True when a live `@room` token survived — the send path sets
   *  `m.mentions.room` so the whole channel is notified. The room
   *  sentinel is never folded into `mentions`. */
  mentionRoom: boolean;
}

/** Sentinel MXID the composer assigns to a live `@room` token. It is
 *  not a real user id — `serialiseFormattedBody` recognises it so the
 *  send path sets `m.mentions.room` instead of a `user_ids` entry. */
export const ROOM_MENTION_MXID = "@room";

/**
 * Detect whether the caret in `value` is currently inside a trigger
 * token. Pure — used by the hook below and by the menu provider for
 * deterministic tests. Returns the active token shape or `null`.
 */
/**
 * Whitespace test that includes ASCII space / tab / newline / carriage
 * return AND non-breaking space (`\u00A0`, common in pasted rich
 * text). The default `/\s/` covers all of these but we keep it
 * explicit so the contract stays auditable.
 */
const WHITESPACE_RE = /[\s\u00A0]/;

export function detectTrigger(value: string, caret: number): AutocompleteState | null {
  if (caret < 0 || caret > value.length) return null;
  // Walk backwards from the caret looking for a trigger character at
  // word start. Bail on whitespace / newline / another trigger before
  // we find one — those mean the caret isn't inside a token.
  for (let i = caret - 1; i >= 0; i -= 1) {
    const ch = value[i];
    if (ch === undefined) return null;
    if (WHITESPACE_RE.test(ch)) return null;
    if (TRIGGERS.includes(ch as AutocompleteTrigger)) {
      // Word-start guard: the trigger must be at the start of the
      // textarea or be preceded by whitespace/newline. Without this
      // an email address (`me@nurion.com`) opens the @ provider.
      const prev = i > 0 ? value[i - 1] : undefined;
      if (prev !== undefined && !WHITESPACE_RE.test(prev)) {
        return null;
      }
      const trigger = ch as AutocompleteTrigger;
      const query = value.slice(i + 1, caret);
      // Reject query containing whitespace — closes the token.
      if (WHITESPACE_RE.test(query)) return null;
      if (query.length < MIN_QUERY_LENGTH[trigger]) {
        return { open: false, trigger, query, triggerIndex: i };
      }
      return { open: true, trigger, query, triggerIndex: i };
    }
  }
  return null;
}

const CLOSED_STATE: AutocompleteState = {
  open: false,
  trigger: null,
  query: "",
  triggerIndex: -1,
};

export interface UseAutocompleteOptions {
  /** Ref to the textarea the autocomplete is bound to. */
  textareaRef: React.RefObject<HTMLTextAreaElement | null>;
  /** Live composer value — same string the textarea displays. */
  value: string;
  /**
   * Callback the hook invokes after a `select` to update the textarea
   * value. The composer wires this to its `setValue`.
   */
  onChangeValue: (next: string) => void;
  /**
   * Optional callback fired once a token is committed via `select`.
   * The composer collects committed mentions into a list keyed by
   * start position and replays them at send time to assemble the
   * `formatted_body` HTML.
   */
  onCommitMention?: (token: MentionToken) => void;
}

export interface UseAutocompleteResult extends AutocompleteState {
  /**
   * Replace the trigger token with `opts.text`. Updates the textarea
   * (via `onChangeValue`), records the mention through `onCommitMention`
   * if `opts.html` is supplied, restores caret position one past the
   * inserted text, and (unless `keepOpen`) closes the menu.
   */
  select: (opts: AutocompleteSelectOptions) => void;
  /** Close the menu without inserting anything. */
  close: () => void;
}

/**
 * Track autocomplete state for a textarea. Reads caret position via
 * `selectionStart` on every input / selection change so a single
 * arrow-key press that exits a token closes the menu — without that
 * the menu would linger after the user clicked elsewhere in the
 * textarea.
 */
export function useAutocomplete({
  textareaRef,
  value,
  onChangeValue,
  onCommitMention,
}: UseAutocompleteOptions): UseAutocompleteResult {
  const [state, setState] = useState<AutocompleteState>(CLOSED_STATE);
  const stateRef = useRef(state);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);
  const onChangeValueRef = useRef(onChangeValue);
  useEffect(() => {
    onChangeValueRef.current = onChangeValue;
  }, [onChangeValue]);
  const onCommitMentionRef = useRef(onCommitMention);
  useEffect(() => {
    onCommitMentionRef.current = onCommitMention;
  }, [onCommitMention]);

  // Re-evaluate the trigger state from the latest `value` + caret on
  // every render where the textarea is mounted. Using `selectionStart`
  // (rather than tracking it ourselves) keeps the hook honest under
  // arrow-key navigation, mouse clicks, and IME selection changes.
  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) {
      if (stateRef.current.open || stateRef.current.trigger !== null) {
        setState(CLOSED_STATE);
      }
      return;
    }
    const caret = ta.selectionStart ?? value.length;
    const next = detectTrigger(value, caret);
    if (!next) {
      if (stateRef.current.open || stateRef.current.trigger !== null) {
        setState(CLOSED_STATE);
      }
      return;
    }
    if (
      next.open === stateRef.current.open &&
      next.trigger === stateRef.current.trigger &&
      next.query === stateRef.current.query &&
      next.triggerIndex === stateRef.current.triggerIndex
    ) {
      return;
    }
    setState(next);
  }, [textareaRef, value]);

  // Listen to selection changes (arrow keys, click) so the menu
  // closes when the caret leaves the active token. `selectionchange`
  // on the document is the only reliable way to observe textarea
  // selection movement that doesn't change the value.
  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    function update() {
      if (!ta) return;
      const caret = ta.selectionStart ?? 0;
      const next = detectTrigger(ta.value, caret);
      if (!next) {
        if (stateRef.current.open || stateRef.current.trigger !== null) {
          setState(CLOSED_STATE);
        }
        return;
      }
      if (
        next.open === stateRef.current.open &&
        next.trigger === stateRef.current.trigger &&
        next.query === stateRef.current.query &&
        next.triggerIndex === stateRef.current.triggerIndex
      ) {
        return;
      }
      setState(next);
    }
    const onBlur = () => {
      // Defer close so a mousedown on the menu's options has a chance
      // to fire `select` before the menu unmounts. The menu calls
      // `e.preventDefault()` on its mousedown so the textarea keeps
      // focus, but we still want to close on a real focus shift.
      setTimeout(() => {
        if (document.activeElement !== ta) setState(CLOSED_STATE);
      }, 0);
    };
    document.addEventListener("selectionchange", update);
    ta.addEventListener("blur", onBlur);
    return () => {
      document.removeEventListener("selectionchange", update);
      ta.removeEventListener("blur", onBlur);
    };
  }, [textareaRef]);

  const close = useCallback(() => {
    setState(CLOSED_STATE);
  }, []);

  const select = useCallback(
    ({ text, html, mxid, ownAgent, keepOpen }: AutocompleteSelectOptions) => {
      const ta = textareaRef.current;
      const current = stateRef.current;
      if (!ta || !current.trigger || current.triggerIndex < 0) return;
      const start = current.triggerIndex;
      const end = start + 1 + current.query.length;
      // `setRangeText` preserves the undo stack — `value =` would
      // wipe it. The fourth argument places the caret one space past
      // the inserted text, then we append a literal space below so
      // the user can immediately keep typing.
      const replacement = `${text} `;
      ta.setRangeText(replacement, start, end, "end");
      const next = ta.value;
      onChangeValueRef.current(next);
      if (html !== undefined) {
        onCommitMentionRef.current?.({
          start,
          end: start + text.length,
          text,
          html,
          mxid,
          ownAgent,
        });
      }
      if (keepOpen) {
        // Tab-to-select keeps focus and re-evaluates against the
        // new textarea content. The selectionchange listener above
        // will re-open the menu on the next caret.
        return;
      }
      setState(CLOSED_STATE);
    },
    [textareaRef],
  );

  return { ...state, select, close };
}

/**
 * Build the Matrix-custom HTML `formatted_body` for a composition with
 * inserted mention tokens. Pure: tokens carry their plain-text and
 * HTML projections; the assembly walks the body splicing each token's
 * HTML into the position it occupies.
 *
 * Tokens that have drifted (i.e. the body no longer contains the
 * recorded plain text at the recorded position) are silently dropped
 * — the user has edited around them and the mention no longer
 * applies. Better to ship a plain-text body than a corrupted pill.
 *
 * Markdown in the body is converted to Matrix HTML; mention pills are
 * spliced in via private-use placeholders so markdown never parses
 * inside a pill.
 */
const PILL_OPEN = "\uE000";
const PILL_CLOSE = "\uE001";
const PILL_RE = /\uE000(\d+)\uE001/g;

export function serialiseFormattedBody(
  body: string,
  tokens: readonly MentionToken[],
): SerialisedComposition {
  // Sort by start index so we can walk left-to-right without
  // worrying about overlapping splices. Filter to tokens whose
  // recorded text still matches the live body — drift means the
  // user typed inside or around the token after committing it.
  const live = tokens
    .filter((t) => body.slice(t.start, t.end) === t.text)
    .sort((a, b) => a.start - b.start);
  let cursor = 0;
  let marked = "";
  const pills: string[] = [];
  const mentions: string[] = [];
  const seen = new Set<string>();
  let mentionRoom = false;
  for (const token of live) {
    if (token.start < cursor) continue;
    marked += body.slice(cursor, token.start);
    marked += `${PILL_OPEN}${pills.length}${PILL_CLOSE}`;
    pills.push(token.html);
    cursor = token.end;
    if (token.mxid === ROOM_MENTION_MXID) {
      mentionRoom = true;
      continue;
    }
    if (token.mxid !== undefined && !seen.has(token.mxid)) {
      seen.add(token.mxid);
      mentions.push(token.mxid);
    }
  }
  marked += body.slice(cursor);
  const markdownHtml = markdownToMatrixHtml(marked);
  if (markdownHtml === null && pills.length === 0) {
    return { body, formattedBody: null, mentions: [], mentionRoom: false };
  }
  const html = (markdownHtml ?? escapeHtml(marked)).replace(
    PILL_RE,
    (_match, index: string) => pills[Number(index)] ?? "",
  );
  return { body, formattedBody: html, mentions, mentionRoom };
}

export function shiftMentions(tokens: readonly MentionToken[], edit: MentionEdit): MentionToken[] {
  if (tokens.length === 0) return [];
  const oldEnd = edit.start + edit.removedLength;
  const delta = edit.insertedLength - edit.removedLength;
  const next: MentionToken[] = [];
  for (const token of tokens) {
    if (token.end <= edit.start) {
      next.push(token);
      continue;
    }
    if (token.start >= oldEnd) {
      next.push({ ...token, start: token.start + delta, end: token.end + delta });
      continue;
    }
  }
  return next;
}

export function realignMentions(body: string, tokens: readonly MentionToken[]): MentionToken[] {
  if (tokens.length === 0) return [];
  let cursor = 0;
  const next: MentionToken[] = [];
  for (const token of [...tokens].sort((a, b) => a.start - b.start)) {
    if (token.start >= cursor && body.slice(token.start, token.end) === token.text) {
      next.push(token);
      cursor = token.end;
      continue;
    }
    const start = body.indexOf(token.text, cursor);
    if (start === -1) continue;
    next.push({ ...token, start, end: start + token.text.length });
    cursor = start + token.text.length;
  }
  return next;
}

/**
 * Minimal HTML escape — `&`, `<`, `>`, `"` only. Sufficient for the
 * `formatted_body` text wrapping a mention pill (the pill itself is
 * a pre-built `<a>` tag). Newlines are preserved as `<br />` so
 * Matrix-custom HTML respects the spec's whitespace rules without us
 * shipping `<p>` paragraphs the picker doesn't need.
 */
function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/\n/g, "<br />");
}
